import Button from "antd/es/button"
import Modal from "antd/es/modal"
import { useCallback, useMemo, useReducer, useState } from "react"

import type { FileData } from "../../documents/types"
import useErrorPopup from "../../hooks/useErrorPopup"
import AdvancedMenu from "./AdvancedMenu"
import ConfirmModalContent from "./ConfirmModalContent"
import WizardContext from "./context"
import { reducer } from "./reducer"
import type { FileDocument } from "./state"
import { initialFileDocumentState, initialState } from "./state"

// Helper function to create a document from FileData without requiring Excel parsing
const fileDataToDocument = (fileData: FileData) => {
  const { fileContents, fileType, fileName, oid } = fileData

  // Create a minimal document structure with just one empty sheet
  return {
    ...initialFileDocumentState,
    kind: "FILE",
    oid,
    mimeType: fileType,
    fileContents,
    fileType,
    fileName,
  } as FileDocument
}

interface AdvancedModalProps {
  fileData: FileData
  onClose: () => void
  handleDocumentProcessing: (document: FileDocument) => Promise<void>
  title: string
}

const AdvancedModal: React.FC<AdvancedModalProps> = ({
  fileData,
  onClose: onParentClose,
  handleDocumentProcessing,
  title,
}) => {
  const populatedInitialState = useMemo(
    () => ({
      ...initialState,
      documents: [fileDataToDocument(fileData)],
      documentIndex: 0,
    }),
    [fileData],
  )

  const [state, dispatch] = useReducer(reducer, populatedInitialState)
  const [modal, contextHolder] = Modal.useModal()
  const [open, setOpen] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const { handleSuccess, handleError } = useErrorPopup()

  const onClose = useCallback(() => {
    onParentClose()
    setOpen(false)
  }, [onParentClose])

  const onCancel = useCallback(() => {
    return modal.confirm({
      title: false,
      icon: <></>,
      content: <ConfirmModalContent />,
      onOk: onClose,
    })
  }, [modal, onClose])

  const onSubmit = useCallback(async () => {
    if (handleDocumentProcessing) {
      setError(undefined)
      setIsSubmitting(true)
      try {
        // Since we only have one document, we can safely cast it to FileDocument
        await handleDocumentProcessing(state.documents[0] as FileDocument)
        handleSuccess("Questionnaire submitted successfully.")
        onClose()
      } catch (error) {
        setError("Could not submit questionnaire, please try again later.")
        handleError({ error, prefix: "Failed to submit questionnaire." })
      } finally {
        setIsSubmitting(false)
      }
    } else {
      onClose()
    }
  }, [
    handleSuccess,
    handleError,
    state.documents,
    handleDocumentProcessing,
    onClose,
  ])

  // We always use the first document since we only have one
  const documentIndex = 0
  const currentDocument = state.documents[0]

  return (
    <WizardContext.Provider value={{ state, dispatch }}>
      {contextHolder}
      <Modal
        open={open}
        footer={false}
        centered={true}
        width="80%"
        destroyOnClose={true}
        closeIcon={false}
        maskClosable={false}
        onCancel={onCancel}
        title={title}
        classNames={{
          body: "flex flex-col p-6",
        }}
      >
        <div className="mb-4">
          {error && <p className="mb-4 text-red-500">{error}</p>}
          <AdvancedMenu
            className="mb-4"
            documentIndex={documentIndex}
            currentDocument={currentDocument}
            dispatch={dispatch}
            showCollapsible={false}
          />
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button
            className="font-bold"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            className="font-bold"
            onClick={onSubmit}
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </div>
      </Modal>
    </WizardContext.Provider>
  )
}

export default AdvancedModal
